import type { PbjBand, PbjCategoryScore } from "../../../utils/models";

// Which set of bounds a value is scored against:
// - "cms"       — the published CMS cutoffs (`minValue`/`maxValue`), what the Scoring
//                 Thresholds ladder prints.
// - "effective" — the safety-margin-tightened cutoffs the scorer actually matched on
//                 (`bufferedMinValue`/`bufferedMaxValue`). Identical to "cms" when the
//                 department's `bandRangeBufferPct` is 0.
export type BandBounds = "cms" | "effective";

// Kind-aware half-open band match — the FE mirror of the backend
// `band_scoring.py::_band_matches`. `hppd`/`admin` bands are `[lo, hi)` (higher is
// better, lower edge inclusive); `turnover` bands are `(lo, hi]` (lower is better,
// upper edge inclusive). `null` on either side denotes an unbounded top/bottom tier.
const bandMatches = (value: number, lo: number | null, hi: number | null, kind: PbjCategoryScore["kind"]): boolean => {
	if (kind === "turnover") {
		if (lo !== null && value <= lo) return false;
		if (hi !== null && value > hi) return false;
		return true;
	}
	if (lo !== null && value < lo) return false;
	if (hi !== null && value >= hi) return false;
	return true;
};

const boundsOf = (band: PbjBand, bounds: BandBounds): [number | null, number | null] =>
	bounds === "cms" ? [band.minValue, band.maxValue] : [band.bufferedMinValue, band.bufferedMaxValue];

// The band `value` lands in on this category's ladder, or null when nothing matched
// (shouldn't happen — the ladder covers the real line). Bands are ordered best-first,
// so the first match wins, exactly like the backend scorer.
export const bandForValue = (category: PbjCategoryScore, value: number, bounds: BandBounds = "cms"): PbjBand | null => {
	for (const band of category.bands) {
		const [lo, hi] = boundsOf(band, bounds);
		if (bandMatches(value, lo, hi, category.kind)) return band;
	}
	return null;
};

// Points `value` earns on this category's ladder. Falls back to the category's own
// score when no band matched, so a caller never renders a fabricated 0.
export const bandPointsForValue = (category: PbjCategoryScore, value: number, bounds: BandBounds = "cms"): number =>
	bandForValue(category, value, bounds)?.points ?? category.score;

// The band this measure's value would land in under the PUBLISHED CMS cutoffs — the
// row a user reads off the Scoring Thresholds table when they check the score by hand.
// With a band-range buffer set this can sit ABOVE the band that was actually scored,
// which is the whole reason the UI has to explain the gap.
export const cmsBandFor = (category: PbjCategoryScore): PbjBand | null => bandForValue(category, category.rawValue, "cms");

// Points the safety-margin band buffer costs this measure: what the CMS cutoffs alone
// would award minus what was actually scored. 0 when no buffer is set (the two ladders
// coincide). Callers must exclude N/A and Invalid measures first — their score is forced
// by that flag, not by the buffer.
export const bufferPointsCost = (category: PbjCategoryScore): number => Math.max(0, bandPointsForValue(category, category.rawValue, "cms") - category.score);

// How a band's buffered cutoff should be phrased. Mirrors the backend
// `constants.py::format_band_display_range` band-by-band, so the buffered column reads
// as the same sentence as the CMS column next to it.
export type BufferedCutoffKind = "at_least" | "below" | "at_most" | "count" | "count_plus";

export type BufferedCutoff = { kind: BufferedCutoffKind; value: string };

// Printed thresholds round AWAY from the achievable side so a displayed cutoff is never
// easier than the real one: an HPPD/admin "≥" cutoff rounds up, a turnover "≤" cutoff
// rounds down. (Same intent as the ceil-to-3dp on the "next goal" diff.) Three decimals
// matches the precision the HPPD measures are read at.
const THRESHOLD_DECIMALS = 1000;

const formatCutoff = (value: number, kind: PbjCategoryScore["kind"]): string => {
	const rounded =
		kind === "turnover" ? Math.floor(value * THRESHOLD_DECIMALS) / THRESHOLD_DECIMALS : Math.ceil(value * THRESHOLD_DECIMALS) / THRESHOLD_DECIMALS;
	// String() drops trailing zeros the way the backend's `%g` formatting does ("4.062", "1.1", "100").
	return String(rounded);
};

// The single cutoff that defines a band under the BUFFERED bounds, or null for a tier
// that has no cutoff on its defining side (the open-ended top/bottom band).
export const bufferedCutoffFor = (band: PbjBand, kind: PbjCategoryScore["kind"]): BufferedCutoff | null => {
	if (kind === "admin") {
		if (band.bufferedMinValue === null) return null;
		return { kind: band.bufferedMaxValue === null ? "count_plus" : "count", value: formatCutoff(band.bufferedMinValue, kind) };
	}
	if (kind === "turnover") {
		if (band.bufferedMaxValue === null) return null;
		return { kind: "at_most", value: formatCutoff(band.bufferedMaxValue, kind) };
	}
	// hppd — higher is better, so the lower bound defines the band; the bottom tier has
	// none and shows its upper boundary for context.
	if (band.bufferedMinValue !== null) return { kind: "at_least", value: formatCutoff(band.bufferedMinValue, kind) };
	if (band.bufferedMaxValue !== null) return { kind: "below", value: formatCutoff(band.bufferedMaxValue, kind) };
	return null;
};
